import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { TwoFactorMethod, User } from "@autfit/shared-types";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logout, reloginSucceeded } from "../../store/slices/authSlice";
import { authAPI } from "../../services/api";
import { clearAllDrafts } from "../../utils/resultDrafts";
import { getLocalizedErrorMessage } from "../../utils/errorHandler";
import Button from "../common/Button";
import Input from "../common/Input";
import ReLoginCodeForm from "./ReLoginCodeForm";

/**
 * Shown over the mounted page when the session is lost (refresh failed).
 * Same steps as the normal login (password, then 2FA code), but the page
 * underneath keeps its state, so entered results can be saved afterwards.
 * Signing in as another user discards drafts and opens the dashboard.
 */
const ReLoginDialog: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const previousUser = useAppSelector((state) => state.auth.user);
  const [email, setEmail] = useState(previousUser?.email ?? "");
  const [password, setPassword] = useState("");
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // "Later": e.g. during a running timer the dialog must not block the
  // page; a banner stays visible until the user signs in.
  const [minimized, setMinimized] = useState(false);

  const finish = (user: User) => {
    const otherUser = !previousUser || previousUser.id !== user.id;
    if (otherUser) clearAllDrafts();
    dispatch(reloginSucceeded(user));
    if (otherUser) navigate("/dashboard", { replace: true });
  };

  const run = async (step: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await step();
    } catch (err: unknown) {
      setError(getLocalizedErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = (e: React.FormEvent) => {
    e.preventDefault();
    run(async () => {
      const response = await authAPI.login({ email, password });
      if (response.requiresTwoFactor) {
        setPendingUserId(response.user.id);
      } else {
        finish(response.user);
      }
    });
  };

  const submitCode = (code: string) => {
    if (!pendingUserId) return;
    run(async () => {
      const response = await authAPI.verify2FA(pendingUserId, {
        code,
        method: TwoFactorMethod.TOTP,
      });
      finish(response.user);
    });
  };

  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        className="fixed left-1/2 top-2 z-[100] -translate-x-1/2 rounded-full bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-lg hover:bg-red-700"
      >
        {t("session.reloginBanner")}
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900 bg-opacity-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="relogin-title"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 id="relogin-title" className="text-lg font-semibold text-gray-900">
          {t("session.reloginTitle")}
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          {t("session.reloginDescription")}
        </p>
        {error && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          </div>
        )}
        {pendingUserId ? (
          <ReLoginCodeForm busy={busy} onSubmit={submitCode} />
        ) : (
          <form onSubmit={submitPassword} className="mt-4 space-y-4">
            <Input
              label={t("auth.emailAddress")}
              aria-label={t("auth.emailAddress")}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Input
              label={t("auth.password")}
              aria-label={t("auth.password")}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
            />
            <p className="text-xs text-gray-500">
              {t("session.reloginOtherUserNote")}
            </p>
            <Button type="submit" className="w-full" isLoading={busy}>
              {t("auth.signIn")}
            </Button>
          </form>
        )}
        <div className="mt-4 border-t pt-3 text-center">
          <button
            type="button"
            className="mr-4 text-sm text-gray-600 underline hover:text-gray-800"
            onClick={() => setMinimized(true)}
          >
            {t("session.reloginLater")}
          </button>
          <button
            type="button"
            className="text-sm text-gray-600 underline hover:text-gray-800"
            onClick={() => dispatch(logout())}
            title={t("session.reloginLogoutWarning")}
          >
            {t("session.reloginLogout")}
          </button>
          <p className="mt-1 text-xs text-gray-400">
            {t("session.reloginLogoutWarning")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReLoginDialog;
